import { atom } from 'jotai';
import {
  useQAPairAICanNotReply,
  useQAPairAIReplyError,
  useQAPairConversationKnowledgeQuery,
} from './useQAPair';
import { isIdEqual } from 'utils/is';
import { useClearKnowledgeQuery } from './useKnowledgeQuery';
import { NIM_CACHE_ID_PREFIX } from './getNimCacheId';
import { useUnmount } from 'ahooks';
import { useCallback } from 'react';
import { useAtomCallback } from 'jotai/utils';
import { useCleanQAPair } from './useCleanQAPair';

export const MAX_QUERY_HUMAN_REPLY_MSG_MAP_SIZE = 3;

/**
 * Q 和 人工回复关联关系 map
 */
const qaPairQueryHumanReplyMsgMapState = atom<Map<string, Set<string>>>(new Map());

export const useSetQAPairQueryHumanReplyMsgMap = () => {
  const canNotReplyPermission = useQAPairAICanNotReply();
  const replyErrorPermission = useQAPairAIReplyError();

  return useAtomCallback(
    useCallback(
      (get, set, queryMsgId: string, humanReplyMsgId: string) => {
        if (!canNotReplyPermission && !replyErrorPermission) {
          return;
        }
        const isNimMsgId = humanReplyMsgId.startsWith(NIM_CACHE_ID_PREFIX);
        if (isNimMsgId) {
          return;
        }

        const mapValue = get(qaPairQueryHumanReplyMsgMapState);

        if (!mapValue.has(queryMsgId)) {
          mapValue.set(queryMsgId, new Set());
        }
        mapValue.get(queryMsgId)?.add(humanReplyMsgId);
        set(qaPairQueryHumanReplyMsgMapState, new Map(mapValue));
      },
      [canNotReplyPermission, replyErrorPermission],
    ),
  );
};

export const useGetQAPairQueryHumanReplyMsgMap = () => {
  return useAtomCallback(
    useCallback((get, _, queryMsgId: string) => {
      return get(qaPairQueryHumanReplyMsgMapState).get(queryMsgId);
    }, []),
  );
};

export const useAutoCleanKnowledgeQueryListener = (conversationId: string) => {
  const conversationKnowledgeQuery = useQAPairConversationKnowledgeQuery();
  const clearKnowledgeQuery = useClearKnowledgeQuery();
  const cleanQAPair = useCleanQAPair();
  const getQAPairQueryHumanReplyMsgMap = useGetQAPairQueryHumanReplyMsgMap();

  useUnmount(() => {
    if (!conversationKnowledgeQuery) {
      return;
    }

    if (isIdEqual(conversationKnowledgeQuery?.conversationId, conversationId)) {
      const { knowledgeQuery, knowledgeReply } = conversationKnowledgeQuery;
      let size = 0;
      if (knowledgeQuery) {
        const queryHumanReplyMsgMap = getQAPairQueryHumanReplyMsgMap(knowledgeQuery.msgId);
        size = queryHumanReplyMsgMap?.size ?? 0;
      }
      if (knowledgeReply) {
        const queryHumanReplyMsgMap = getQAPairQueryHumanReplyMsgMap(knowledgeReply.msgId);
        size += queryHumanReplyMsgMap?.size ?? 0;
      }
      if (size >= MAX_QUERY_HUMAN_REPLY_MSG_MAP_SIZE) {
        cleanQAPair();
        clearKnowledgeQuery(conversationId);
      }
    }
  });
};
